import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Building2, User, MapPin, Loader2, ShoppingCart, CheckCircle, Mail, Gift, FileCheck } from "lucide-react";
import { apiErrorText } from "../lib/api";
import { PrivacyPolicyLink } from "../components/PrivacyPolicyLink";
import { isValidSiren, legalCheckboxParts, legalTextLanguage, normalizeSiren } from "../lib/legal-acceptance-texts";
import { loadCurrentLegalDocuments, publicLegalPdfUrl, type CurrentLegalDocuments } from "../hooks/useCurrentLegalDocuments";
import { SIGNUP_REQUIRED_KINDS } from "../lib/legal-documents";
import kestioLogoWhite from "../assets/kestio-logo-white.png";

function LogoHeader() {
  return (
    <div className="flex items-end justify-center gap-3 mb-4 pb-1">
      <img src={kestioLogoWhite} alt="Kestio" className="h-8 invert" />
      <span
        className="font-bold text-2xl relative"
        style={{ fontFamily: "Sora, sans-serif", color: "#384872", top: "8px" }}
      >
        BizCoach
      </span>
    </div>
  );
}

// --- Thank You Page (after Zoho payment) ---
function ThankYouPage() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <LogoHeader />
          <div className="flex justify-center mb-4">
            <CheckCircle className="h-16 w-16 text-green-500" />
          </div>
          <CardTitle className="text-xl" style={{ fontFamily: "Sora, sans-serif" }}>
            {t("signup.thankYouTitle")}
          </CardTitle>
          <CardDescription className="text-base mt-2">
            {t("signup.thankYouDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Mail className="h-5 w-5" />
            <p>{t("signup.activationEmailSent")}</p>
          </div>
          <p className="text-sm text-muted-foreground">
            {t("signup.activationInstructions")}
          </p>
          <p className="text-xs text-muted-foreground mt-4">
            {t("signup.emailNotReceived")}<a href="/" className="text-blue-500 underline">{t("signup.connectHere")}</a>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// --- Signup Form ---
export function SignupPage() {
  const { t, i18n } = useTranslation();

  // Check if this is the thank-you page
  if (window.location.pathname === "/signup/thank-you") {
    return <ThankYouPage />;
  }

  const params = new URLSearchParams(window.location.search);
  const planCode = params.get("plan") || "";
  const planLabelKey = `signup.planLabels.${planCode}`;
  const translatedPlan = t(planLabelKey);
  const planLabel = translatedPlan === planLabelKey ? planCode : translatedPlan;
  const invitationToken = params.get("invitation_token") || "";
  // Pack one-shot (lien landing /buy/:tier-one-shot → ?oneshot=1) : la quantité
  // saisie = nombre de PACKS (et non de licences). Adapte le wording du bloc.
  const isOneShot = params.get("oneshot") === "1";
  // Plafond de quantité de la fiche produit (cf. buildSignupRedirect). À 1, le
  // sélecteur n'a plus de sens : c'est le cas du pack d'essai offert, dont on ne
  // veut qu'un exemplaire. L'écrêtage qui fait foi est côté serveur.
  const maxQuantity = Number(params.get("maxqty")) || null;
  const quantityLocked = maxQuantity === 1;

  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    companyName: "",
    phone: "",
    address: "",
    postalCode: "",
    city: "",
    quantity: 1,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Identification de la société (décision D2) et acceptation des CGV/CGU + DPA (plan
  // feat-acceptation-cgv-dpa, lot 3). Cases jamais cochées par défaut.
  const [siren, setSiren] = useState("");
  const [foreignCompany, setForeignCompany] = useState(false);
  const [foreignRegistration, setForeignRegistration] = useState("");
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [dpaAccepted, setDpaAccepted] = useState(false);
  const [legalDocs, setLegalDocs] = useState<CurrentLegalDocuments | null>(null);
  const [legalLoadFailed, setLegalLoadFailed] = useState(false);
  // Même chargement que le lien vers la politique de confidentialité (un seul appel par page) ;
  // `force` relit le serveur (« Réessayer », documents mis à jour pendant la saisie).
  const loadLegalDocs = async (force = false) => {
    setLegalLoadFailed(false);
    const docs = await loadCurrentLegalDocuments(force);
    if (docs) setLegalDocs(docs);
    else setLegalLoadFailed(true);
  };
  useEffect(() => { void loadLegalDocs(); }, []);
  const legalLang = legalTextLanguage(i18n.language);
  // Création de compte fermée tant qu'un document obligatoire (CGV/CGU, DPA) n'a pas de version en vigueur.
  const legalClosed = !!legalDocs && SIGNUP_REQUIRED_KINDS.some((k) => !legalDocs[k]);
  const sirenDigits = normalizeSiren(siren);
  const sirenOk = isValidSiren(siren);
  const registrationOk = foreignCompany ? foreignRegistration.trim().length > 0 : sirenOk;
  const canSubmit = !!legalDocs?.cgv && !!legalDocs?.dpa && cgvAccepted && dpaAccepted && registrationOk;
  const companyInText = form.companyName.trim() || t("signup.legal.companyFallback");

  // Mode invitation (cf. spec/trial-mode-with-coupon.md — essai gratuit).
  // Si l'URL contient ?invitation_token=XXX, on résout le token côté serveur
  // pour pré-remplir email + afficher le bandeau essai. L'email est verrouillé
  // (sécurité : empêche de détourner le lien pour une autre adresse).
  const [invitation, setInvitation] = useState<{
    prospectEmail: string;
    prospectFirstName: string | null;
    prospectName: string | null;
    licenceName: string;
    sessionsLimit: number;
    inviter: string | null;
  } | null>(null);
  const [invitationLoading, setInvitationLoading] = useState(!!invitationToken);
  const [invitationError, setInvitationError] = useState<string | null>(null);

  useEffect(() => {
    if (!invitationToken) return;
    (async () => {
      try {
        const res = await fetch(`/api/trial-invitations/resolve?token=${encodeURIComponent(invitationToken)}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Lien d'invitation invalide.");
        const inv = data.invitation;
        setInvitation({
          prospectEmail: inv.prospectEmail,
          prospectFirstName: inv.prospectFirstName,
          prospectName: inv.prospectName,
          licenceName: inv.licence?.name || "",
          sessionsLimit: inv.sessionsLimit,
          inviter: inv.inviter,
        });
        // Pré-remplit email (verrouillé) + prénom/nom fournis par l'offre d'essai
        setForm((f) => ({
          ...f,
          email: inv.prospectEmail,
          quantity: 1,
          firstName: inv.prospectFirstName?.trim() || f.firstName,
          lastName: inv.prospectName?.trim() || f.lastName,
        }));
      } catch (err: any) {
        setInvitationError(err.message);
      } finally {
        setInvitationLoading(false);
      }
    })();
  }, [invitationToken]);

  const update = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // En mode invitation : on envoie invitationToken (le backend résout
      // licence + coupon + email en interne). Sinon : flow standard avec planCode.
      const legalPayload = {
        siren: foreignCompany ? "" : sirenDigits,
        foreignCompany,
        foreignRegistration: foreignCompany ? foreignRegistration.trim() : "",
        language: legalLang,
        legal: { cgvAccepted, dpaAccepted, cgvVersionId: legalDocs?.cgv?.id, dpaVersionId: legalDocs?.dpa?.id },
      };
      const payload = invitationToken
        ? { ...form, ...legalPayload, invitationToken }
        : { ...form, ...legalPayload, planCode };
      const res = await fetch("/api/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        // Documents mis à jour pendant la saisie : on recharge la version en vigueur et on
        // décoche, pour que la personne accepte ce qu'elle a sous les yeux.
        if (data?.code === "legal_documents_changed") {
          setCgvAccepted(false);
          setDpaAccepted(false);
          void loadLegalDocs(true);
        }
        throw new Error(apiErrorText(data, t("signup.errorCreatingAccount")));
      }
      if (data.hostedPageUrl) {
        window.location.href = data.hostedPageUrl;
      } else {
        setError(t("signup.errorPaymentUrlMissing"));
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Écran de chargement pendant la résolution du token d'invitation
  if (invitationToken && invitationLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <LogoHeader />
            <div className="flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#384872]" /></div>
            <CardDescription className="mt-2">Validation de votre invitation…</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Token d'invitation invalide / expiré / déjà utilisé
  if (invitationToken && invitationError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <LogoHeader />
            <CardTitle>Invitation invalide</CardTitle>
            <CardDescription>{invitationError}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground">
              Contactez votre interlocuteur Kestio pour obtenir un nouveau lien, ou
              <a href="/" className="text-blue-500 underline"> connectez-vous</a> si vous avez déjà un compte.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Ni plan ni invitation → page "plan non spécifié"
  if (!planCode && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <LogoHeader />
            <CardTitle>{t("signup.planNotSpecified")}</CardTitle>
            <CardDescription>
              {t("signup.choosePlan")}<a href="https://bizcoach.kestio.com/#pricing" className="text-blue-500 underline">{t("signup.pricingPage")}</a>.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <LogoHeader />
          <CardTitle className="text-xl" style={{ fontFamily: "Sora, sans-serif" }}>
            {t("signup.createAccount")}
          </CardTitle>
          {invitation ? (
            <div className="mt-3 rounded-lg border-2 border-[#7CBF94] bg-[#7CBF94]/10 p-4 text-left">
              <div className="flex items-start gap-2">
                <Gift className="h-5 w-5 text-[#1E7A3C] shrink-0 mt-0.5" />
                <div className="text-sm text-[#1E7A3C]">
                  <p className="font-semibold">
                    {invitation.inviter ? `${invitation.inviter} vous offre un essai gratuit !` : "Vous bénéficiez d'un essai gratuit !"}
                  </p>
                  <p className="mt-1">
                    <strong>{invitation.sessionsLimit} simulations offertes</strong> sur l'abonnement <strong>{invitation.licenceName}</strong>.
                    Aucun débit pendant la période d'essai — vous décidez ensuite.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <CardDescription>
              {t("signup.selectedPlan")} <strong>{planLabel}</strong>
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Contact */}
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              <User className="h-4 w-4" /> {t("signup.sectionContact")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="firstName">{t("signup.firstNameLabel")}</Label>
                <Input id="firstName" value={form.firstName} onChange={update("firstName")} required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="lastName">{t("signup.lastNameLabel")}</Label>
                <Input id="lastName" value={form.lastName} onChange={update("lastName")} required />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="email">{t("signup.emailLabel")}</Label>
              <Input id="email" type="email" autoComplete="email" value={form.email} onChange={update("email")} required placeholder={t("signup.emailPlaceholder")} readOnly={!!invitation} className={invitation ? "bg-slate-100 cursor-not-allowed" : ""} />
              {invitation && <p className="text-xs text-muted-foreground">{t("signup.emailLockedByInvitation")}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="phone">{t("signup.phoneLabel")}</Label>
              <Input id="phone" type="tel" value={form.phone} onChange={update("phone")} />
            </div>

            {/* Entreprise */}
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide mt-6">
              <Building2 className="h-4 w-4" /> {t("signup.sectionCompany")}
            </div>
            <div className="space-y-1">
              <Label htmlFor="companyName">{t("signup.companyNameLabel")}</Label>
              <Input id="companyName" value={form.companyName} onChange={update("companyName")} required />
            </div>
            {foreignCompany ? (
              <div className="space-y-1">
                <Label htmlFor="foreignRegistration">{t("signup.legal.foreignRegistrationLabel")}</Label>
                <Input id="foreignRegistration" value={foreignRegistration} maxLength={50} onChange={(e) => setForeignRegistration(e.target.value)} required />
              </div>
            ) : (
              <div className="space-y-1">
                <Label htmlFor="siren">{t("signup.legal.sirenLabel")}</Label>
                <Input id="siren" inputMode="numeric" autoComplete="off" value={siren} onChange={(e) => setSiren(e.target.value)} required
                  placeholder={t("signup.legal.sirenPlaceholder")} aria-invalid={sirenDigits.length >= 9 && !sirenOk} aria-describedby="siren-help" />
                <p id="siren-help" className="text-xs" style={{ color: sirenDigits.length >= 9 && !sirenOk ? "#C0423F" : undefined }}>
                  {sirenDigits.length >= 9 && !sirenOk ? t("signup.legal.sirenInvalid") : <span className="text-muted-foreground">{t("signup.legal.sirenHelp")}</span>}
                </p>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm" style={{ color: "#384872" }}>
              <input id="foreignCompany" type="checkbox" checked={foreignCompany} onChange={(e) => setForeignCompany(e.target.checked)} className="h-4 w-4" />
              {t("signup.legal.foreignCompanyLabel")}
            </label>

            {/* Adresse */}
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide mt-6">
              <MapPin className="h-4 w-4" /> {t("signup.sectionAddress")}
            </div>
            <div className="space-y-1">
              <Label htmlFor="address">{t("signup.addressLabel")}</Label>
              <Input id="address" value={form.address} onChange={update("address")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="postalCode">{t("signup.postalCodeLabel")}</Label>
                <Input id="postalCode" value={form.postalCode} onChange={update("postalCode")} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="city">{t("signup.cityLabel")}</Label>
                <Input id="city" value={form.city} onChange={update("city")} />
              </div>
            </div>

            {/* Quantité — masquée en mode invitation (l'essai est mono-utilisateur)
                et sur un produit plafonné à 1 (pack d'essai offert). */}
            {!invitation && !quantityLocked && (
            <>
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide mt-6">
              <ShoppingCart className="h-4 w-4" /> {t(isOneShot ? "signup.sectionPacks" : "signup.sectionLicences")}
            </div>
            <div className="rounded-lg border-2 border-blue-200 bg-blue-50/50 p-4 space-y-2">
              <Label htmlFor="quantity" className="text-sm font-medium">
                {t(isOneShot ? "signup.packsQuestion" : "signup.licencesQuestion")}
              </Label>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-10 w-10 text-lg font-bold"
                  onClick={() => setForm((f) => ({ ...f, quantity: Math.max(1, f.quantity - 1) }))}
                >
                  −
                </Button>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  max="999"
                  className="w-20 text-center text-lg font-semibold"
                  value={form.quantity}
                  onChange={(e) => setForm((f) => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-10 w-10 text-lg font-bold"
                  onClick={() => setForm((f) => ({ ...f, quantity: f.quantity + 1 }))}
                >
                  +
                </Button>
                <span className="text-sm text-muted-foreground">{form.quantity > 1 ? t(isOneShot ? "signup.packMany" : "signup.licenceMany") : t(isOneShot ? "signup.packOne" : "signup.licenceOne")}</span>
              </div>
              <p className="text-xs text-muted-foreground">{t(isOneShot ? "signup.packHelp" : "signup.licenceHelp")}</p>
            </div>
            </>
            )}

            {/* CGV/CGU et DPA : deux cases non cochées, un lien pour lire chaque document (PDF
                français, qui fait foi). Le texte de chaque case est celui que le serveur enregistre. */}
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide mt-6">
              <FileCheck className="h-4 w-4" /> {t("signup.legal.section")}
            </div>
            {legalLoadFailed ? (
              <div role="alert" className="rounded-lg p-3 text-sm" style={{ background: "#FBE9E7", color: "#C0423F" }}>
                {t("signup.legal.loadError")}{" "}
                <button type="button" className="font-semibold underline" onClick={() => void loadLegalDocs(true)}>{t("common.retry")}</button>
              </div>
            ) : !legalDocs ? (
              <div className="flex justify-center py-3"><Loader2 className="h-5 w-5 animate-spin text-[#384872]" /></div>
            ) : legalClosed ? (
              <div role="alert" className="rounded-lg p-3 text-sm" style={{ background: "#FFF6DB", color: "#926B00" }}>{t("signup.legal.closed")}</div>
            ) : (
              <div className="space-y-3 rounded-lg border p-4" style={{ borderColor: "#E6E4EE" }}>
                {legalLang === "en" && <p className="text-xs text-muted-foreground">{t("signup.legal.frenchOnly")}</p>}
                {([
                  { kind: "cgv" as const, doc: legalDocs.cgv!, checked: cgvAccepted, set: setCgvAccepted },
                  { kind: "dpa" as const, doc: legalDocs.dpa!, checked: dpaAccepted, set: setDpaAccepted },
                ]).map(({ kind, doc, checked, set }) => {
                  // Le nom du document, dans la phrase, ouvre le PDF de la version proposée (nouvel
                  // onglet) : on peut le lire avant de cocher. Un lien dans un <label> n'active pas la case.
                  const parts = legalCheckboxParts(kind, legalLang, { company: companyInText });
                  return (
                    <label key={doc.kind} className="flex items-start gap-2 text-sm" style={{ color: "#384872" }}>
                      <input id={`accept-${doc.kind}`} type="checkbox" checked={checked} onChange={(e) => set(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        {parts.before}
                        <a href={publicLegalPdfUrl(doc.id)} target="_blank" rel="noopener noreferrer" className="font-medium underline" style={{ color: "#5A4BBE" }}>{parts.link}</a>
                        {parts.after}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            {/* Politique de confidentialité : information, pas de case (13/10/2026). */}
            <PrivacyPolicyLink className="text-xs text-muted-foreground" />

            {error && <p className="text-sm text-red-500">{error}</p>}

            <Button type="submit" className="w-full" disabled={isLoading || !canSubmit}>
              {isLoading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("signup.redirectingToPayment")}</>
              ) : (
                invitation ? "Démarrer mon essai gratuit" : t("signup.goToPayment")
              )}
            </Button>

            {!canSubmit && !!legalDocs && !legalClosed && (
              <p className="text-xs text-center text-muted-foreground">{t("signup.legal.submitHint")}</p>
            )}

            <p className="text-xs text-center text-muted-foreground">
              {t("signup.alreadyHaveAccount")} <a href="/" className="text-blue-500 underline">{t("signup.loginLink")}</a>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
